import { useEffect, useRef } from "react";
import {
  Controller,
  ControllerProps,
  Path,
  useFormContext,
  useWatch,
} from "react-hook-form";

import {
  Alert,
  Divider,
  FormControlLabel,
  FormHelperText,
  Switch,
} from "@mui/material";

import _ from "lodash";
import { stateNames } from "src/utility/constants";
import { INT32_MAX, intValidation, isInt32 } from "src/utility/inputHelpers";

import {
  CheckboxInput,
  DateInput,
  TextInput,
} from "@components/Forms/ControlledInputs";
import { DefaultTextField } from "@components/Forms/DefaultInputs";
import CountrySelector from "@components/Utility/Selectors/CountrySelector";
import { State } from "@models/State";
import { SubState, isReyesSubState } from "@models/SubState";
import { ControlledResourceAutocomplete } from "@utils/forms/ControlledResourceAutocomplete";
import { useHasPermission } from "@utils/permissions";

import { AddressFormValues, usesZipCode } from "../addressFormHelpers";
import { useValidateUniqueAddressName } from "../useValidateUniqueAddressName";

// Support both 5-digit and 9-digit zip codes
const ZIP_CODE_PATTERN = /^\d{5}(-?\d{4})?$/;

// Country-dependent checks must live in `validate`: react-hook-form merges
// re-registered rules, so a conditionally omitted `pattern` would persist.
const zipValidationRules: ControllerProps<AddressFormValues, "zip">["rules"] = {
  required: "Zip Code cannot be empty",
  validate: (zip, values) =>
    values.country !== "USA" ||
    ZIP_CODE_PATTERN.test(zip ?? "") ||
    "Must be a valid Zip Code",
};

const EXTERNAL_REYES_ID_ERROR = `Reyes ID must be a whole number between 1 and ${INT32_MAX}`;

const externalReyesIdValidationRules = {
  ...intValidation,
  required: "Reyes ID is required",
  validate: (value: string) =>
    (isInt32(value) && Number(value) >= 1) || EXTERNAL_REYES_ID_ERROR,
};

export type AddressFieldsProps = {
  nameLabel: string;
  phoneRequired?: boolean;
  /** Disables standard address fields (name, street, city, state, etc.). */
  disabled?: boolean;
  /** Overrides phone disabled state. When omitted, phone follows `disabled`. */
  disablePhone?: boolean;
  showAbn?: boolean;
  showCountryField?: boolean;
  /** Extra disable for country (e.g. distributor country permission). */
  disableCountry?: boolean;
  showChangeRequestFields?: boolean;
  showIsActiveToggle?: boolean;
  usesTerritorySubState?: boolean;
  validateUniqueName?: boolean;
  /** Scopes unique-name validation to addresses in this territory. */
  territoryId?: string | null;
  isEditing: boolean;
  showReyesFields?: boolean;
};

/**
 * Presentational address fields. Must be rendered inside a react-hook-form
 * {@link FormProvider} by a workflow-specific wrapper.
 */
export const AddressFields = ({
  nameLabel,
  phoneRequired,
  disabled: coreDisabled = false,
  disablePhone,
  showAbn,
  showCountryField,
  disableCountry = false,
  showChangeRequestFields,
  showIsActiveToggle,
  usesTerritorySubState,
  validateUniqueName,
  territoryId,
  isEditing,
  showReyesFields,
}: AddressFieldsProps) => {
  const hasPermission = useHasPermission();
  const canCreateReyesAddresses = hasPermission(
    "address:create_reyes_addresses",
    false
  );

  const {
    control,
    setValue,
    clearErrors,
    trigger,
    formState: { defaultValues },
  } = useFormContext<AddressFormValues>();

  const stateId = useWatch({ name: "stateId" });
  const stateHasSubStateOptions = useWatch({ name: "stateHasSubStateOptions" });

  const phoneDisabled = disablePhone ?? coreDisabled;

  const clearReyesFormFieldsUnlessLocked = () => {
    if (defaultValues?.isForReyes) return;
    setValue("isForReyes", false);
    setValue("externalReyesId", "");
    clearErrors("externalReyesId");
  };

  const handleStateChange = (state: State | null) => {
    const hasSubStateOptions = Boolean(state?.hasSubStateOptions);
    setValue("stateHasSubStateOptions", hasSubStateOptions);

    // Reset sub-state when state changes
    setValue("subStateId", "");
    setValue("isReyesSubState", false);
    clearErrors("subStateId");
    clearReyesFormFieldsUnlessLocked();
  };

  const handleSubStateChange = (subState: SubState | null) => {
    const reyesSubState = isReyesSubState(subState);
    setValue("isReyesSubState", reyesSubState);
    if (!reyesSubState) {
      clearReyesFormFieldsUnlessLocked();
    }
  };

  const reg = <TName extends Path<AddressFormValues>>(
    name: TName,
    rules?: ControllerProps["rules"],
    fieldDisabled?: boolean
  ) => ({
    name,
    control,
    rules,
    disabled: fieldDisabled ?? coreDisabled,
  });

  const country = useWatch({ control, name: "country" });
  const zipDisabled = !usesZipCode(country);

  // Country / state / sub-state are disabled in edit mode
  const locationLocked = isEditing;

  const handleCountryChange = (newCountry: string) => {
    if (newCountry === country) return;
    setValue("stateId", "");
    setValue("subStateId", "");
    setValue("stateHasSubStateOptions", false);
    setValue("isReyesSubState", false);
    if (!usesZipCode(newCountry)) {
      setValue("zip", "");
      clearErrors("zip");
    }
    clearReyesFormFieldsUnlessLocked();
    clearErrors(["stateId", "subStateId", "country"]);
  };

  // Zip and state validity depend on country; re-run validation on the
  // affected fields so isValid catches up, without
  // surfacing errors on name, street, and the rest the user hasn't reached.
  const prevCountry = useRef(country);
  useEffect(() => {
    if (prevCountry.current === country) return;
    prevCountry.current = country;
    void trigger(["zip", "stateId", "subStateId"]);
  }, [country, trigger]);

  useValidateUniqueAddressName({
    shouldValidate: validateUniqueName ?? false,
    territoryId,
  });

  return (
    <div className="flex flex-col gap-6 mt-2">
      <TextInput
        {...reg("name", { required: "Name cannot be empty" })}
        label={nameLabel}
      />
      {showAbn && (
        <TextInput
          {...reg("abn", { required: "ABN is required" })}
          label="ABN"
        />
      )}
      <TextInput {...reg("defaultAttn")} label="Attention" />
      <TextInput
        {...reg("streetAddress1", { required: "Address 1 cannot be empty" })}
        label="Street 1"
      />
      <TextInput {...reg("streetAddress2")} label="Street 2" />
      <div className="grid grid-cols-5 gap-6">
        <TextInput
          {...reg("city", { required: "City cannot be empty" })}
          label="City"
          className="flex-1 col-span-2"
        />
        <ControlledResourceAutocomplete
          resource="states"
          label="State *"
          {...reg(
            "stateId",
            { required: "State cannot be empty" },
            coreDisabled || locationLocked
          )}
          getOptionText={(state) => stateNames[state.code] ?? state.code ?? ""}
          tapOptions={(opts) =>
            _.chain(opts)
              .sortBy((o) => stateNames[o.code] ?? o.code)
              .value()
          }
          requestParams={{ filter: { country } }}
          onChange={(_e, state) => handleStateChange(state)}
          className="col-span-2"
        />
        <Controller<AddressFormValues, "zip">
          name="zip"
          control={control}
          disabled={coreDisabled || zipDisabled}
          rules={zipValidationRules}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <DefaultTextField
              {...field}
              label="Zip Code"
              inputRef={ref}
              value={field.value ?? ""}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
        {usesTerritorySubState && stateHasSubStateOptions && stateId && (
          <ControlledResourceAutocomplete
            resource="sub-states"
            label="Substate *"
            {...reg(
              "subStateId",
              { required: "Sub-state cannot be empty" },
              coreDisabled || locationLocked
            )}
            requestParams={{
              filter: { stateId, isControl: false },
            }}
            onChange={(_e, v) => handleSubStateChange(v)}
            className="col-span-2 col-start-3"
          />
        )}
      </div>
      {showCountryField && (
        <CountrySelector
          control={control}
          name="country"
          rules={{ required: "Country cannot be empty" }}
          disabled={coreDisabled || locationLocked || disableCountry}
          onChange={handleCountryChange}
        />
      )}
      <TextInput
        {...reg(
          "phoneNumber",
          phoneRequired ? { required: "Phone number is required" } : undefined,
          phoneDisabled
        )}
        label="Phone"
        placeholder="XXX-XXX-XXXX"
      />
      {showReyesFields && canCreateReyesAddresses && <ReyesFields />}
      {showChangeRequestFields && (
        <>
          <Divider />
          <DateInput
            label="Effective Date"
            {...reg("changeEffectiveDate", {
              required: "Effective date is required",
            })}
          />
          <TextInput
            {...reg("changeNote", { required: "Change note is required" })}
            label="Change Note"
          />
        </>
      )}
      {showIsActiveToggle && (
        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value ?? true}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label={field.value ? "Active" : "Inactive"}
            />
          )}
        />
      )}
    </div>
  );
};

const REYES_CHECKBOX_HELPER =
  "Checking this box means that Reyes will be notified of all orders shipping to this address";
const REYES_LOCKED_HELPER =
  "Reyes is being notified for all orders with this address";
const REYES_IRREVERSIBLE_WARNING =
  "Once you mark an address for Reyes, you cannot remove this setting later.";

const ReyesFields = () => {
  const {
    control,
    formState: { defaultValues },
  } = useFormContext<AddressFormValues>();
  const isForReyes = useWatch({ name: "isForReyes" });
  const isReyesSubState = useWatch({ name: "isReyesSubState" });

  if (!isReyesSubState) return null;

  // Once an address is saved as being for Reyes, it cannot be changed
  const isReyesLocked = defaultValues?.isForReyes === true;

  return (
    <div className="flex flex-col gap-1">
      {isReyesLocked ? (
        <TextInput
          name="externalReyesId"
          control={control}
          label="Reyes ID"
          rules={externalReyesIdValidationRules}
          helperText={REYES_LOCKED_HELPER}
          className="flex-1"
        />
      ) : (
        <>
          <div className="flex gap-2">
            <CheckboxInput
              name="isForReyes"
              control={control}
              label="Is for Reyes"
              className="flex-shrink-0"
            />
            {isForReyes && (
              <TextInput
                name="externalReyesId"
                control={control}
                rules={externalReyesIdValidationRules}
                autoFocus
                label="Reyes ID"
                className="flex-1"
              />
            )}
          </div>
          <FormHelperText className="mt-0">
            {REYES_CHECKBOX_HELPER}
          </FormHelperText>
          {isForReyes && (
            <Alert severity="warning" className="-mx-3">
              {REYES_IRREVERSIBLE_WARNING}
            </Alert>
          )}
        </>
      )}
    </div>
  );
};
